import { useState } from 'react';
import clsx from 'clsx';

import { getInactiveAssetIconFromSchemaMarkerId } from '@atom/map';
import { Checkbox, Collapse, Icon, IconButton, Switch } from '@atom/mui';
import {
  KeyboardArrowDownIcon,
  KeyboardArrowUpIcon,
  LocationOnIcon,
} from '@atom/mui/icons';
import { toggleFromSet } from '@atom/utilities/setUtilities';

import { useMapLayers } from '../MapPortalDataProvider';

import InventoryFolderFilter from './InventoryFolderFilter';

import classes from './filterPanel.module.css';

const AssetsSection = () => {
  const [expanded, setExpanded] = useState<boolean>(true);
  const { layers, schemas, setLayer, setFilter } = useMapLayers();
  const { assets: toggled, assetSchemas, categoryIds } = layers;

  const toggleAssetLayer = (schemaId: string) => {
    setFilter('assetSchemas', toggleFromSet(assetSchemas, schemaId));
  };

  const selectAll = () => {
    setFilter('assetSchemas', new Set(schemas.map(({ id }) => id)));
  };

  const clearAll = () => {
    setFilter('assetSchemas', new Set());
  };

  const allSelected = schemas.every(({ id }) => assetSchemas.has(id));
  const noneSelected = assetSchemas.size === 0;

  return (
    <div className={classes.sectionContainer}>
      <div className={classes.sectionHeaderContainer}>
        <Switch
          label={
            <div className={classes.toggleLabel}>
              <div className={classes.sectionHeaderIcon}>
                <Icon icon={LocationOnIcon} />
              </div>
              <div>Inventory</div>
            </div>
          }
          checked={toggled}
          onChange={() => setLayer('assets', !toggled)}
          data-cy="dashboardInventoryToggle"
        />
        <IconButton
          edge="end"
          onClick={() => setExpanded(!expanded)}
          data-cy="dashboardInventoryExpand"
        >
          <Icon icon={expanded ? KeyboardArrowUpIcon : KeyboardArrowDownIcon} />
        </IconButton>
      </div>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <div className={classes.sectionFilters}>
          <div className={classes.selectAllContainer}>
            <div
              onClick={() => {
                if (toggled && !allSelected) {
                  selectAll();
                }
              }}
              className={clsx(
                classes.selectAllLabel,
                (!toggled || allSelected) && classes.disabled,
              )}
              data-cy="dashboardInventorySelectAll"
            >
              Select All
            </div>
            <div className={classes.selectAllDivider} />
            <div
              onClick={() => {
                if (toggled && !noneSelected) {
                  clearAll();
                }
              }}
              className={clsx(
                classes.selectAllLabel,
                (!toggled || noneSelected) && classes.disabled,
              )}
              data-cy="dashboardInventoryClearAll"
            >
              Clear All
            </div>
          </div>
          {schemas.map(schema => {
            const icon = getInactiveAssetIconFromSchemaMarkerId(
              schema.markerId,
            );

            return (
              <div className={classes.sectionFilterRow} key={schema.id}>
                <Checkbox
                  edge="start"
                  disabled={!toggled}
                  checked={assetSchemas.has(schema.id)}
                  onChange={() => toggleAssetLayer(schema.id)}
                  data-cy={`dashboardInventoryFilter-${schema.assetType}`}
                />
                <img className={classes.sectionFilterRowIcon} src={icon} />
                <span className={classes.sectionFilterRowText}>
                  {schema.assetType}
                </span>
              </div>
            );
          })}
          <div className={classes.filterContainer}>
            <InventoryFolderFilter
              toggled={toggled}
              categoryIds={categoryIds}
              onCategoryIdsUpdate={ids => setFilter('categoryIds', ids)}
            />
          </div>
        </div>
      </Collapse>
    </div>
  );
};

export default AssetsSection;
